import { uid } from '../../../../lib/uid';

/**
 * Test data backing the Autogenerate button on the position form.
 *
 * Fills every required field so the primary action enables on the first try. Keep it in
 * lockstep with the form: a new required field must be added here in the same change, or
 * Autogenerate silently stops working.
 *
 * Values are synthetic. Never a real person's designation copied from a production plantilla
 * — a generated record is written to the database, so whatever is here ends up in the system.
 */

/** Generic civil-service designations. None of them names a real post at a real employer. */
const DESIGNATIONS = [
  'Administrative Aide',
  'Administrative Officer',
  'Project Evaluation Officer',
  'Records Officer',
  'Supply Officer',
  'Information Systems Analyst',
  'Statistician',
  'Planning Officer',
];

const ROMAN = ['I', 'II', 'III', 'IV', 'V'];

function pick<T>(values: readonly T[]): T {
  return values[Math.floor(Math.random() * values.length)];
}

/** Six characters of a uid, which is enough to keep repeated clicks from colliding. */
function suffix(): string {
  return uid().slice(0, 6).toLowerCase();
}

/** Generic expectations, true of no particular post. */
const EXPECTATIONS = [
  'Willing to render work beyond office hours when required.',
  'Able to travel to field offices on short notice.',
  'Comfortable preparing written reports in English and Filipino.',
];

/** Generic qualification-standard lines, true of no particular post. */
const EDUCATION = [
  "Bachelor's degree relevant to the job",
  'Completion of two years of college studies',
  "Bachelor's degree in Public Administration",
];
const TRAINING: { text: string; hours: number | null }[] = [
  { text: 'Relevant training', hours: 4 },
  { text: 'Records management', hours: 8 },
  { text: 'Customer service', hours: null },
];
const EXPERIENCE = [
  'One year of relevant experience',
  'Two years of relevant experience',
  'Supervisory experience',
];

/** One or two distinct lines from a list — a standard is a LIST, so the sample should be one. */
function some<T>(values: readonly T[]): T[] {
  return [...values].sort(() => Math.random() - 0.5).slice(0, 1 + Math.floor(Math.random() * 2));
}

export interface SamplePosition {
  title: string;
  code: string;
  education: string[];
  training: { text: string; hours: number | null }[];
  experience: string[];
  otherExpectations: string;
}

/**
 * One catalog title — every field the form requires, plus other expectations.
 *
 * The title and code carry a suffix: each is unique among the ACTIVE rows, and Autogenerate
 * is most useful clicked repeatedly — without one the second click would produce a title the
 * server rejects as a duplicate.
 *
 * The grade and the eligibility are deliberately not here: picking either honestly means
 * picking from a catalog the form has already loaded rather than inventing an id — so the
 * form does that, where the catalog is. The description is left alone for the plainer reason
 * that filling an optional prose field puts words on the record that nobody wrote.
 */
export function samplePosition(): SamplePosition {
  const unique = suffix();

  return {
    title: `${pick(DESIGNATIONS)} ${pick(ROMAN)} ${unique}`,
    code: `POS-${unique.toUpperCase()}`,
    otherExpectations: pick(EXPECTATIONS),
    education: some(EDUCATION),
    training: some(TRAINING),
    experience: some(EXPERIENCE),
  };
}
